import { CommonModule } from '@angular/common';
import {
  Component,
  QueryList,
  ViewChildren,
  computed,
  inject,
  effect,
  signal,
  AfterViewInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import {
  ISessionEntry,
  VbLxpCheckboxComponent,
  VbLxpSwitchComponent,
  TimespanPipe,
  TimingTowerComponent,
  VbLxpPanelComponent,
  VbLxpVideoSlotComponent,
  WindowSizeService,
  VideoLayout,
  VideoLayoutService,
  FeatureSettingsService,
  PanelService,
  StreamData,
  StreamService,
  RaceSessionService,
  RaceClassComponent,
} from '@vbrick/vb-lxp-lib';

import { LivestreamViewService } from './livestream-view.service';
import { SelectionCoordinatorService } from './selection-coordinator.service';
import { TwitchChatModule } from './twitch-chat/twitch-chat.module';

const LAYOUT_CLASS_MAP: Record<VideoLayout, string> = {
  [VideoLayout.SINGLE]: 'aspect-16/9',
  [VideoLayout.SIDE_BY_SIDE]: 'aspect-32/9',
  [VideoLayout.LARGE_LEFT]: 'aspect-48/18',
  [VideoLayout.LARGE_RIGHT]: 'aspect-48/18',
  [VideoLayout.GRID_2X2]: 'aspect-16/9',
  [VideoLayout.STACKED]: 'aspect-16/18',
};

@Component({
  selector: 'app-livestream-view',
  imports: [
    CommonModule,
    TimespanPipe,
    RouterModule,
    VbLxpCheckboxComponent,
    VbLxpPanelComponent,
    VbLxpSwitchComponent,
    VbLxpVideoSlotComponent,
    TwitchChatModule,
    TimingTowerComponent,
    RaceClassComponent,
  ],
  templateUrl: './livestream-view.component.html',
  styleUrl: './livestream-view.component.css',
  host: {
    '[style.height]': `'100%'`,
  },
})
export class LivestreamViewComponent implements AfterViewInit {
  @ViewChildren('livestreamPlayerSlot')
  currentLivestreamPlayerSlots: QueryList<VbLxpVideoSlotComponent>;

  public layoutService = inject(VideoLayoutService);
  public raceSessionService = inject(RaceSessionService);
  public selectionCoordinatorService = inject(SelectionCoordinatorService);
  public streamService = inject(StreamService);

  private windowSizeService = inject(WindowSizeService);
  private panelService = inject(PanelService);
  private featureSettingsService = inject(FeatureSettingsService);
  private livestreamViewService = inject(LivestreamViewService);

  public VideoLayout = VideoLayout;
  public isMobile = toSignal(this.windowSizeService.isMobile$);
  public moreColumns = signal(false);
  public isMainStreamSelected = signal(true);
  public raceStatus = toSignal(this.raceSessionService.raceStatus$);
  public timeStatus = toSignal(this.raceSessionService.timeStatus$);
  public featureSettings = toSignal(this.featureSettingsService.featureSettings$);
  public selectedClasses = signal<string[]>([]);

  protected readonly containerAspectRatioClass = computed(
    () => LAYOUT_CLASS_MAP[this.layoutService.activeLayout()],
  );

  constructor() {
    effect(() => {
      this.layoutService.activeLayout();
      setTimeout(() => {
        const currentPlayers = this.currentLivestreamPlayerSlots
          .toArray()
          .map((lps) => lps.mvPlayer);
        this.livestreamViewService.updateCurrentPlayers(currentPlayers);
      });
    });
    effect(() => {
      if (this.moreColumns()) {
        this.panelService.closePanel('chat');
      }
    });

    effect(() => {
      const chatOpen = this.chatPanelOpen();
      if (chatOpen && this.moreColumns()) {
        this.panelService.closePanel('timing');
        setTimeout(() => this.panelService.openPanel('chat'));
      }
    });

    effect(() => {
      const timingOpen = this.timingPanelOpen();
      if (timingOpen && this.moreColumns()) {
        this.panelService.closePanel('chat');
        setTimeout(() => this.panelService.openPanel('timing'));
      }
    });
  }

  private chatPanelOpen = toSignal(this.panelService.getPanelState('chat'));
  private timingPanelOpen = toSignal(this.panelService.getPanelState('timing'));

  ngAfterViewInit(): void {
    const currentPlayers = this.currentLivestreamPlayerSlots.toArray().map((lps) => lps.mvPlayer);
    this.livestreamViewService.updateCurrentPlayers(currentPlayers);
  }

  public openFullTimingPage() {
    const currentQueryParams = window.location.search;
    const newRoute = '/full-timing';
    const fullUrl = `${newRoute}${currentQueryParams}`;

    window.open(fullUrl, '_blank');
  }

  public onTimingTowerSelectionAdded(team: ISessionEntry) {
    this.livestreamViewService.addSlotItem({
      type: 'timing',
      id: team.number,
      streamData: this.streamService.getStreamDataByCarNumber(team.number),
    });
  }

  public onTimingTowerSelectionRemoved(team: ISessionEntry) {
    this.livestreamViewService.removeSlotItem({
      type: 'timing',
      id: team.number,
      streamData: this.streamService.getStreamDataByCarNumber(team.number),
    });
  }

  public onMainStreamToggled(isSelected: boolean) {
    if (isSelected) {
      this.livestreamViewService.addSlotItem({ type: 'mainStream' });
      this.streamService.isMainStreamSelected.set(true);
    } else {
      this.livestreamViewService.removeSlotItem({ type: 'mainStream' });
      this.streamService.isMainStreamSelected.set(false);
    }
  }

  public onTwitchStreamToggled(isSelected: boolean) {
    if (isSelected) {
      this.livestreamViewService.addSlotItem({ type: 'twitchStream' });
      this.streamService.isTwitchStreamSelected.set(true);
    } else {
      this.livestreamViewService.removeSlotItem({ type: 'twitchStream' });
      this.streamService.isTwitchStreamSelected.set(false);
    }
  }

  public onPlayerClosed(streamData: StreamData) {
    if (streamData.carNumber) {
      this.livestreamViewService.removeSlotItem({
        type: 'timing',
        id: streamData.carNumber,
        streamData,
      });
    } else if (streamData.id === 'main-stream') {
      this.livestreamViewService.removeSlotItem({
        type: 'mainStream',
      });
    } else {
      this.livestreamViewService.removeSlotItem({
        type: 'twitchStream',
      });
    }
  }
}
